/**
 * Modal dialog implementation.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright © Ircam Amplify 2025
 */

import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'

import ActionButton from '@/components/Button/ActionButton'
import DisabledByDefaultRoundedIcon from '@mui/icons-material/DisabledByDefaultRounded'
import { Breakpoint } from '@mui/material'
import { ReactElement } from 'react'
import './ModalDialog.css'

export interface ModalDialogProps {
  id?: string
  title: string
  subtitle?: string
  description?: string
  contents: ReactElement
  isVisible: boolean
  changeVisible: (isVisible: boolean) => void
  className?: string
  contentsClassName?: string

  showTopRighCloseButton?: boolean
  showOkButton?: boolean
  showCancelButton?: boolean
  onCancel?: () => void
  onOk?: () => void

  /* Available max width: "xs"|"sm"|"md"|"lg"|false */
  maxWidth?: Breakpoint | false
}

const ModalDialog = ({
  showTopRighCloseButton = true,
  showOkButton = false,
  showCancelButton = false,
  ...props
}: ModalDialogProps) => {
  return (
    <Dialog
      open={props.isVisible}
      onClose={() => props.changeVisible(false)}
      className={`modal-dialog-root ${props.className}`}
      disableScrollLock
      fullWidth
      maxWidth={props.maxWidth ?? ('lg' as Breakpoint)}
      scroll="paper"
      slotProps={{
        paper: {
          id: props.id,
        },
      }}
    >
      <div className="modal-dialog-visible-content">
        {/* Top-right close button */}
        {showTopRighCloseButton && (
          <button
            onClick={() => props.changeVisible(false)}
            className="modal-dialog-close"
            aria-label="Close"
          >
            <DisabledByDefaultRoundedIcon />
          </button>
        )}

        {/* Title */}
        <DialogTitle className="modal-dialog-title">{props.title}</DialogTitle>
        {props.subtitle && (
          <h3 className="modal-dialog-subtitle">{props.subtitle}</h3>
        )}

        {/* Dialog actual content */}
        <DialogContent className="modal-dialog-panel">
          {props.description && (
            <div className="modal-dialog-description">{props.description}</div>
          )}
          <div
            className={`modal-dialog-contents ${props.contentsClassName ?? ''}`}
          >
            {props.contents}
          </div>
        </DialogContent>

        {/* OK and Cancel built-in buttons */}
        <div className="button-container">
          {showCancelButton && (
            <ActionButton
              id="cancel"
              className="outlined"
              onClick={() => {
                props.changeVisible(false)
                if (props.onCancel) {
                  props.onCancel()
                }
              }}
            >
              Cancel
            </ActionButton>
          )}

          {showOkButton && (
            <ActionButton
              id="ok"
              onClick={() => {
                props.changeVisible(false)
                if (props.onOk) {
                  props.onOk()
                }
              }}
            >
              OK
            </ActionButton>
          )}
        </div>
      </div>
    </Dialog>
  )
}

export default ModalDialog
